import {
  ColDef,
  ColGroupDef,
  Column,
  ICellEditorParams,
  NewValueParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import dayjs from 'dayjs';
import { isString } from 'lodash-es';

import { AgExpandableGroupHeaderComponent } from '@widgets/ag-expandable-group-header/ag-expandable-group-header.component';

import { AgDatePickerComponent } from '@shared/ag-components/ag-date-picker/ag-date-picker.component';
import { ZERO_HYPHEN } from '@shared/constants/format';
import { InvoicePrepaidCandidate, PrepaidType } from '@shared/services/gql.service';
import { isValidDateFormat } from '@shared/utils/is-valid-date-format';
import { Utils } from '@shared/utils/utils';

import { GridConfigOptions } from './grid-config-context';
import { AgInvoiceDropdownComponent } from '../../ag-invoice-dropdown/ag-invoice-dropdown.component';
import { AdjustmentsToPrepaidRow } from '../adjustments-to-prepaid.model';

// Helper function to create column definition
const createColumnDef = (
  field: keyof AdjustmentsToPrepaidRow,
  headerName: string,
  options: GridConfigOptions,
  additionalProps: Partial<ColDef<AdjustmentsToPrepaidRow>> = {}
): ColDef<AdjustmentsToPrepaidRow> => {
  const {
    getCellClasses,
    isCellEditable,
    onCellValueChanged,
    recalculateCells,
    tooltipValueGetter,
  } = options;

  return {
    cellClass: getCellClasses(),
    editable: (params) => isCellEditable(params),
    field,
    headerName,
    onCellValueChanged: (params) => {
      recalculateCells(params);
      onCellValueChanged(params);
    },
    tooltipValueGetter: (params) => tooltipValueGetter(params),
    width: 120,
    ...additionalProps,
  };
};

function setInvoiceIdValue(
  params: NewValueParams<AdjustmentsToPrepaidRow>,
  invoiceOptions: InvoicePrepaidCandidate[],
  flags: { journalEntryApManualClearing: boolean },
  currentOpenMonth: string
): boolean {
  const newValue = isString(params.newValue)
    ? invoiceOptions.find(({ id }: { id: string }) => id === params.newValue)
    : params.newValue;

  // Stricter validation: block unpaid invoices for Increase type if feature flag is on
  const { prepaid_type } = params.data;

  if (
    prepaid_type === PrepaidType.PREPAID_TYPE_INCREASE &&
    flags?.journalEntryApManualClearing &&
    newValue?.id &&
    (!newValue.payment_date ||
      dayjs(newValue.payment_date).isAfter(dayjs(currentOpenMonth).endOf('month')))
  ) {
    return false;
  }

  if (newValue?.id) {
    params.data.invoice_id = newValue.id;
    params.data.invoice_no = newValue.invoice_no;
    params.data.accrual_period = newValue.accrual_period;
    params.data.invoice_total = newValue.amount_total;
    params.data.invoice_date = newValue.invoice_date;
    params.data.payment_date = newValue.payment_date;
  } else {
    params.data.invoice_id = null;
    params.data.invoice_no = null;
    params.data.accrual_period = null;
    params.data.invoice_total = null;
    params.data.invoice_date = null;
    params.data.payment_date = null;
  }

  return true;
}

const createInvoiceIdColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { checkError, currentOpenMonth, flags, getCellClasses, invoiceOptions } = options;

  return createColumnDef('invoice_id', 'Invoice #', options, {
    cellClass: getCellClasses(['remove-padding-in-edit-mode', 'ag-flat-dropdown']),
    cellClassRules: {
      'border-aux-error': (params) => checkError(params, 'increase_no_invoice'),
    },
    cellEditor: AgInvoiceDropdownComponent,
    cellEditorParams: {
      options: invoiceOptions.length > 0 ? [{ id: null }, ...invoiceOptions] : [],
    },
    comparator: (valueA, valueB, nodeA, nodeB) => {
      // row sorting
      if (nodeA && nodeB) {
        return (nodeA?.data?.invoice_no || '')
          .toLowerCase()
          .localeCompare((nodeB?.data?.invoice_no || '').toLowerCase());
      }

      // filter sorting
      const valueAInvoice = invoiceOptions.find(({ id }) => id === valueA);
      const valueBInvoice = invoiceOptions.find(({ id }) => id === valueB);

      return (valueAInvoice?.invoice_no || '')
        .toLowerCase()
        .localeCompare((valueBInvoice?.invoice_no || '').toLowerCase());
    },
    filterParams: {
      valueFormatter: (params: ValueFormatterParams<AdjustmentsToPrepaidRow>) => {
        const value = params.value;

        if (value) {
          const invoice = invoiceOptions.find(({ id }) => id === value);

          return invoice?.invoice_no || ZERO_HYPHEN;
        }

        return ZERO_HYPHEN;
      },
    },
    minWidth: 200,
    sort: 'asc',
    sortIndex: 1,
    suppressFillHandle: true,
    valueFormatter: (params) => {
      return (params.value && params.data?.invoice_no) || ZERO_HYPHEN;
    },
    valueSetter: (params) => setInvoiceIdValue(params, invoiceOptions, flags, currentOpenMonth),
  });
};

const createAccrualPeriodColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { checkError, currentOpenMonth, getCellClasses } = options;

  return createColumnDef('accrual_period', 'Posting Period', options, {
    cellClass: getCellClasses(['remove-padding-in-edit-mode', 'text-right']),
    cellClassRules: {
      'border-aux-error': (params) => checkError(params, 'accrual_period_closed_month'),
    },
    cellDataType: 'text',
    cellEditor: AgDatePickerComponent,
    cellEditorParams: (params: ICellEditorParams) => ({
      ignoreValidations: true,
      type: 'month',
      value: params.value || currentOpenMonth,
    }),
    filterParams: {
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? dayjs(params.value).format('MMMM YYYY') : ZERO_HYPHEN;
      },
    },
    headerTooltip: 'Posting Period',
    minWidth: 195,
    sort: 'desc',
    sortIndex: 0,
    sortingOrder: ['desc', 'asc', null],
    suppressSizeToFit: true,
    valueFormatter: (params) => {
      return params.value ? dayjs(params.value).format('MMMM YYYY') : ZERO_HYPHEN;
    },
    valueSetter: (params) => {
      if (params.newValue === null) {
        params.data.accrual_period = null;

        return true;
      }

      if (isValidDateFormat(params.newValue)) {
        params.data.accrual_period = dayjs(params.newValue).format('YYYY-MM-DD');

        return true;
      }

      return false;
    },
    width: 195,
  });
};

const createInvoiceTotalColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { getCellClasses, vendorCurrency } = options;

  return createColumnDef('invoice_total', 'Invoice Total', options, {
    cellClass: getCellClasses(['text-right']),
    filter: 'agNumberColumnFilter',
    headerTooltip: 'Invoice Total',
    minWidth: 125,
    valueFormatter: (params) => {
      return Utils.agCurrencyFormatter(params, vendorCurrency);
    },
    width: 125,
  });
};

const createTotalExpenseColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { getCellClasses, vendorCurrency } = options;

  return createColumnDef('total_expense_per_invoice', 'Total Expense per Invoice', options, {
    cellClass: getCellClasses(['text-right']),
    cellDataType: 'number',
    editable: false,
    filter: 'agNumberColumnFilter',
    headerTooltip: 'Total Expense per Invoice',
    minWidth: 220,
    valueFormatter: (params) => {
      return Utils.agCurrencyFormatter(params, vendorCurrency);
    },
    width: 250,
  });
};

const createInvoiceDateColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { getCellClasses } = options;

  return createColumnDef('invoice_date', 'Invoice Date', options, {
    cellClass: getCellClasses(['text-right']),
    cellDataType: false,
    cellEditor: AgDatePickerComponent,
    headerTooltip: 'Invoice Date',
    minWidth: 195,
    suppressSizeToFit: true,
    valueFormatter: (params) => {
      return params.value ? dayjs(params.value).format('MM/DD/YYYY') : ZERO_HYPHEN;
    },
    valueSetter: (params) => {
      if (params.newValue === null) {
        params.data.invoice_date = null;

        return true;
      }

      if (isValidDateFormat(params.newValue)) {
        params.data.invoice_date = dayjs(params.newValue).format('YYYY-MM-DD');

        return true;
      }

      return false;
    },
    width: 195,
  });
};

const createPaymentDateColumn = (options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> => {
  const { checkError, getCellClasses } = options;

  return createColumnDef('payment_date', 'Payment Date', options, {
    cellClass: getCellClasses(['text-right']),
    cellClassRules: {
      'border-aux-error': (params) => checkError(params, 'payment_date_closed_month'),
    },
    cellDataType: false,
    cellEditor: AgDatePickerComponent,
    headerTooltip: 'Payment Date',
    minWidth: 195,
    suppressSizeToFit: true,
    valueFormatter: (params) => {
      return params.value ? dayjs(params.value).format('MM/DD/YYYY') : ZERO_HYPHEN;
    },
    valueSetter: (params) => {
      if (params.newValue === null) {
        params.data.payment_date = null;

        return true;
      }

      if (isValidDateFormat(params.newValue)) {
        params.data.payment_date = dayjs(params.newValue).format('YYYY-MM-DD');

        return true;
      }

      return false;
    },
    width: 195,
  });
};

export const invoiceDetailsColumnGroup = (
  options: GridConfigOptions
): (ColDef<AdjustmentsToPrepaidRow> | ColGroupDef<AdjustmentsToPrepaidRow>)[] => {
  return [
    {
      children: [
        createInvoiceIdColumn(options),
        createAccrualPeriodColumn(options),
        createInvoiceTotalColumn(options),
        createTotalExpenseColumn(options),
        createInvoiceDateColumn(options),
        createPaymentDateColumn(options),
      ],
      headerClass: ['header-marker', 'text-center'],
      headerGroupComponent: AgExpandableGroupHeaderComponent,
      headerGroupComponentParams: {
        collapsedByDefault: true,
        expandableCols: [
          'invoice_total',
          'total_expense_per_invoice',
          'invoice_date',
          'payment_date',
        ],
        filterCols: (column: Column) => {
          return !['accrual_period', 'invoice_id'].includes(column.getId());
        },
        localStorageKey: 'adjustments_to_prepaid_invoice_details',
      },
      headerName: 'Invoice Details',
      suppressStickyLabel: true,
    },
  ];
};
